import { useMemo, type ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import type { CartPanelCartQueryStatus } from '../utils/cartPanelUtils'

interface Props {
  className?: string
  cartQueryStatus: Extract<CartPanelCartQueryStatus, 'stale' | 'failed'>
  isRetrying: boolean
  onRetry: () => void
}

function CartLoadErrorBanner({ className, cartQueryStatus, isRetrying, onRetry }: Props) {
  const intl = useIntl()

  const retryButtonProps = useMemo<ComponentProps<typeof MessageBanner>['primaryActionButtonProps']>(() => ({
    children: intl.formatMessage({
      id: 'B_3qB6oLwyODv3qd4HvX',
      defaultMessage: 'Try again',
      description: 'Action on the trip planner cart banner that retries loading the cart after it failed to load',
    }),
    onClick: onRetry,
    disabled: isRetrying,
    'aria-busy': isRetrying,
    startIcon: isRetrying ? <LoadingSpinner inline size={16} /> : undefined,
    size: 'small',
  }), [intl, isRetrying, onRetry])

  const staleLabel = intl.formatMessage({
    id: '1ehFBzvxMVqNZJXA_Fe_',
    defaultMessage: 'We couldn’t refresh your cart. Some details may be out of date.',
    description: 'Trip planner cart banner shown when refreshing the cart failed and the last loaded cart details are still displayed',
  })
  const emptyLabel = intl.formatMessage({
    id: '7--2ABhWdi55Kr-2Wo4Q',
    defaultMessage: 'We couldn’t load your cart.',
    description: 'Trip planner cart banner shown when the cart could not be loaded and no cart details are available',
  })

  return (
    <div className={className} role="alert">
      <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />} primaryActionButtonProps={retryButtonProps}>
        {cartQueryStatus === 'stale' ? staleLabel : emptyLabel}
      </MessageBanner>
    </div>
  )
}

export default CartLoadErrorBanner
